import { DRAFT_STATUS } from 'FRONTEND/utils/constants';
import { fetchError } from 'FRONTEND/utils/errors';
import { getConsultationEventsPath, getConsultationPath } from 'FRONTEND/utils/routes';

const formatInnerAttachments = (obj) => {
  if (obj) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => {
      const firstValue = value[0];
      const isAttachmentArray = firstValue !== null && Array.isArray(value) && typeof firstValue === 'object' && Object.hasOwn(firstValue, 'file_type');
      if (key === 'attachments' || isAttachmentArray) {
        return [key, value.map(({ id }) => id?.toString()).filter((id) => id)];
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        return [key, formatInnerAttachments(value)];
      }
      return [key, value];
    }));
  }
};

const formatAttachment = ({
  category,
  created_at: date,
  creator_alias: origin,
  description,
  file_name: fileName,
  file_type: fileType,
  group,
  link,
  transition_name: transitionLabel,
  id,
}) => {
  if (!id) {
    // When adding an attachment to an online draft, it has no id nor link when the server responds. Ignoring the not-yet processed file is ok since the device already has it locally.
    return null;
  }
  const fileNameParts = fileName.split('.');
  const fileExtension = fileName.split('.')[fileNameParts.length - 1];
  // VR: need to refactor this as it was when backend will return the correct value for project attachments
  // const { fileUrl } = await fetchBlobUrl(`${getEnvVariables().CloudUri}${link}`, id);
  return {
    category,
    date,
    origin,
    description,
    fileName,
    fileType,
    fileExtension,
    group,
    path: link,
    transitionLabel,
    id: id.toString(),
    // cloudLink: link,
  };
};

const formatEvent = ({
  created_at: date,
  creator_alias: creatorAlias,
  creator_id: creatorId,
  event_type: type,
  exams: rawExams,
  extra,
  has_attachments: hasAttachments,
  id,
}) => {
  const exams = formatInnerAttachments(rawExams);
  return {
    date,
    creatorAlias,
    creatorId,
    type,
    exams,
    extra,
    hasAttachments,
    id,
  };
};

// TODO: factorize with patient api ?
const formatPatient = (rawPatient) => {
  if (rawPatient == null) {
    return {};
  }
  if (typeof rawPatient !== 'object') {
    return {
      fullName: rawPatient,
    };
  }
  const {
    anonymous: isAnonymous,
    birthdate,
    full_name: fullName,
    gender,
    nationality,
    patient_id: id,
    tenant_id: tenantId,
  } = rawPatient;
  return {
    isAnonymous,
    birthdate,
    fullName,
    gender,
    nationality,
    id,
    tenantId,
  };
};

const formatParticipants = (rawParticipants, currentUserId) => rawParticipants.filter((participant) => participant !== currentUserId);
const formatParticipantsRequest = (participantsRequest) => participantsRequest.reduce((requestedParticipantIdList, { request_to_id: requestedParticipantId, group_ids: requestedGroupIdList }) => {
  if (requestedGroupIdList?.length) {
    return [...new Set([...requestedParticipantIdList, ...requestedGroupIdList])];
  } else {
    return [...new Set([...requestedParticipantIdList, requestedParticipantId])];
  }
}, []);

const formatSurveillance = (monitoredMetrics) => {
  if (!monitoredMetrics) {
    return;
  }
  const eventMap = new Map();

  // Iterate over the metrics to build the base structure
  Object.entries(monitoredMetrics).forEach(([examType, examList]) => {
    examList.forEach((exam) => {
      Object.entries(exam.history).forEach(([timestamp, historyItem]) => {
        const eventId = exam.monitoring_event_id;
        // Initialize or retrieve the event.
        // This groups multiple metrics for the same exam under a single event.
        if (!eventMap.has(eventId)) {
          eventMap.set(eventId, {
            id: eventId,
            victimId: exam.victim_id,
            type: exam.type,
            origin: exam.origin,
            examName: exam.exam_name,
            values: {},
            modificationHistory: [],
            monitoringEventId: exam.monitoring_event_id,
          });
        }

        const event = eventMap.get(eventId);

        if (!event.values[exam.exam_name]) {
          event.values[exam.exam_name] = {};
        }

        if (exam.metric_name === 'surveillance_hours') {
          event.date = new Date(historyItem.value).getTime();
        } else if (!event.date) {
          event.date = new Date(exam.updated_at).getTime();
        }

        const valueObject = {
          value: historyItem.value,
        };

        if (historyItem.unit) {
          valueObject.unit = historyItem.unit;
        }
        if (historyItem.loinc) {
          valueObject.loinc = historyItem.loinc;
        }
        if (historyItem.qualifier) {
          valueObject.qualifier = historyItem.qualifier;
        }
        // If the exam is marked as deleted, we add the deletedByUser information to the valueObject to display it explicitely
        if (exam.is_deleted) {
          valueObject.deletedByUser = { firstName: historyItem?.user_first_name, lastName: historyItem?.user_last_name };
        }

        event.values[exam.exam_name][exam.metric_name] = valueObject;

        // Add to modification history
        const modificationEntry = event.modificationHistory.find(
          entry => entry.modificationDate === parseInt(timestamp),
        );

        if (!modificationEntry) {
          const newModification = {
            modificationDate: parseInt(timestamp),
            modifiedBy: {
              lastName: historyItem.user_last_name,
              firstName: historyItem.user_first_name,
            },
            values: {
              [exam.exam_name]: {
                [exam.metric_name]: valueObject,
              },
            },
            isPending: false,
          };
          event.modificationHistory.push(newModification);
        } else {
          if (!modificationEntry.values[exam.exam_name]) {
            modificationEntry.values[exam.exam_name] = {};
          }
          modificationEntry.values[exam.exam_name][exam.metric_name] = valueObject;
        }
      });
    });
  });

  eventMap.forEach((event) => {
    event.modificationHistory.sort((a, b) => a.modificationDate - b.modificationDate);
    // The last modificationHistory values are current values
    // Need to copy to make sure we do not mixe value from historyItem
    if (event.modificationHistory.length > 0) {
      event.values = structuredClone(
        event.modificationHistory[event.modificationHistory.length - 1].values,
      );
    }
  });
  const result = Array.from(eventMap.values());

  return result;
};

export const formatConsultationAndAttachments = ({
  id,
  consultation_id: consultationId,
  participants: participantsIds = [],
  participant_requests: participantsRequest = [],
  status,
  workflow: type, // FIXME: we need this
  patient: rawPatient,
  last_event: lastEvent,
  attachments: rawAttachmentList,
  downloads, // TODO: format
  mission_name: missionName,
  project_name: projectName,
  events: rawEventList,
  unread,
  transitions,
  currentUserId,
  workflow,
  workflow_version: version,
  monitored_metrics: surveillance = [],
  consultation_timezone: consultationTimezone,
  is_testing: isTesting,
}) => {
  const eventList = rawEventList?.map(formatEvent) ?? [];
  const attachmentList = rawAttachmentList?.map(formatAttachment).filter((attachment) => attachment) ?? [];
  const surveillanceList = formatSurveillance(surveillance);
  const formattedConsultation = {
    id: parseInt(id),
    consultationId,
    type,
    patient: formatPatient(rawPatient),
    lastEvent,
    status,
    attachmentIdList: attachmentList.map(({ id }) => id),
    downloads,
    participants: formatParticipants(participantsIds, currentUserId),
    participantsRequestList: formatParticipantsRequest(participantsRequest),
    eventList,
    missionName,
    unread,
    projectName,
    transitions,
    isDraft: status === DRAFT_STATUS,
    version,
    workflow,
    surveillanceList,
    consultationTimezone,
    isTesting,
  };
  Object.keys(formattedConsultation).forEach(key => {
    if (formattedConsultation[key] === undefined) {
      delete formattedConsultation[key];
    }
  });
  return { consultation: formattedConsultation, attachmentList };
};

export const getConsultation = async(consultationParams, currentUserId, userRightsArray) => {
  try {
    const response = await fetch(getConsultationPath(consultationParams));
    const rawConsult = await response.json();
    // `id` comes from the payload itself (see views/consultations/consultation_detail_view.py): on canonical
    // parsys_id URLs there is no numeric consultation id to pass in from the caller.
    return formatConsultationAndAttachments({
      ...rawConsult,
      currentUserId,
    });
  } catch (error) {
    throw fetchError({ error });
  }
};

export const getConsultationEvents = async(consultationParams) => {
  try {
    const response = await fetch(getConsultationEventsPath(consultationParams));
    return await response.text();
  } catch (error) {
    console.log('getConsultationEvents error', error);
  }
};
